import {Alert} from 'src/design/atoms/Alert';
import {IconInfoCircle} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import {useAppSelector} from 'src/redux/hooks';

function hashMessage(message: string): string {
  let hash = 0;
  for (let i = 0; i < message.length; i++) {
    hash = (hash << 5) - hash + message.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

function getDismissKey(instanceId: number, message: string): string {
  return `proact-banner-dismissed-${instanceId}-${hashMessage(message)}`;
}

function checkDismissed(
  instanceId: number | undefined,
  message: string | undefined
): boolean {
  if (!instanceId || !message) return false;
  try {
    return localStorage.getItem(getDismissKey(instanceId, message)) === 'true';
  } catch {
    return false;
  }
}

export const InstanceBanner: React.FC = () => {
  const instance = useAppSelector(state => state.session.instance);
  const message = instance?.bannerMessage;
  const instanceId = instance?.id;

  const dismissKey = useMemo(
    () => (message && instanceId ? getDismissKey(instanceId, message) : null),
    [message, instanceId]
  );

  const [isDismissed, setIsDismissed] = useState(() =>
    checkDismissed(instanceId, message)
  );

  // Reset dismiss state when banner message or instance changes
  useEffect(() => {
    setIsDismissed(checkDismissed(instanceId, message));
  }, [dismissKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!message || !instanceId || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(getDismissKey(instanceId, message), 'true');
    } catch {
      // localStorage may be unavailable in restricted contexts
    }
    setIsDismissed(true);
  };

  return (
    <Alert
      color="blue"
      variant="light"
      title="Notice"
      icon={<IconInfoCircle />}
      withCloseButton
      onClose={handleDismiss}
      p="xs"
      mb="md"
      aria-live="polite">
      {message}
    </Alert>
  );
};
